import React, { Component } from 'react';
import axios from '../../global/axios';
import ThemeUploadPicker from './theme_upload_picker';

const SLIDE_TYPE_LABELS = {
    point: 'Body / Sermon',
    scripture: 'Scripture',
    song: 'Song Lyrics',
};

class ThemesLayout extends Component {
    constructor(props) {
        super(props);
        this.state = {
            themes: [],
            loading: true,
            uploading: false,
            parsedTheme: null,
        };
    }

    componentDidMount() {
        this._mounted = true;
        this.fetchThemes();
    }

    componentWillUnmount() {
        this._mounted = false;
    }

    fetchThemes = () => {
        this.setState({ loading: true });
        axios.get('/slides/themes.json')
            .then(res => {
                if (this._mounted) this.setState({ themes: res.data, loading: false });
            })
            .catch(() => {
                if (this._mounted) this.setState({ loading: false });
            });
    }

    handleFileUpload = (file) => {
        this.setState({ uploading: true });
        const formData = new FormData();
        formData.append('file', file);

        axios.post('/slides/themes/parse', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        })
            .then(res => {
                this.setState({ parsedTheme: res.data, uploading: false });
            })
            .catch(err => {
                alert('Failed to parse theme: ' + (err.response?.data?.error || err.message));
                this.setState({ uploading: false });
            });
    }

    handleSaveTheme = (themeData) => {
        axios.post('/slides/themes.json', themeData, {
            headers: { 'Content-Type': 'application/json' }
        })
            .then(() => {
                this.setState({ parsedTheme: null });
                this.fetchThemes();
            })
            .catch(err => {
                alert('Failed to save theme: ' + (err.response?.data?.error || err.message));
            });
    }

    handleCancelUpload = () => {
        this.setState({ parsedTheme: null });
    }

    handleActivate = (themeId) => {
        axios.put('/slides/themes/' + themeId + '/activate.json')
            .then(() => this.fetchThemes())
            .catch(err => alert('Failed to activate: ' + err.message));
    }

    handleDelete = (themeId, themeName) => {
        if (!window.confirm('Delete theme "' + themeName + '"?')) return;
        axios.delete('/slides/themes/' + themeId + '.json')
            .then(() => this.fetchThemes())
            .catch(err => alert('Failed to delete: ' + err.message));
    }

    rgbaString(color) {
        if (!color) return '#ffffff';
        const r = Math.round((color.r || 0) * 255);
        const g = Math.round((color.g || 0) * 255);
        const b = Math.round((color.b || 0) * 255);
        return 'rgb(' + r + ',' + g + ',' + b + ')';
    }

    renderSlidePreview(slide) {
        // ProPresenter canvas is 1920x1080; preview is 192x108 (1/10 scale)
        const CANVAS_W = 1920;
        const CANVAS_H = 1080;
        const PREVIEW_W = 192;
        const PREVIEW_H = 108;
        const scale = PREVIEW_W / CANVAS_W; // 0.1

        return (
            <div
                key={slide.slide_type || slide.name}
                className="border border-gray-200 rounded-sm bg-gray-800"
                style={{ width: PREVIEW_W, height: PREVIEW_H, position: 'relative', overflow: 'hidden' }}
            >
                <div style={{
                    position: 'absolute',
                    left: (slide.position?.x || 0) * scale,
                    top: (slide.position?.y || 0) * scale,
                    width: (slide.size?.width || CANVAS_W) * scale,
                    height: (slide.size?.height || CANVAS_H) * scale,
                    fontFamily: (slide.font_family || 'sans-serif') + ', sans-serif',
                    fontSize: Math.max((slide.font_size || 48) * scale, 6) + 'px',
                    color: this.rgbaString(slide.text_color),
                    textAlign: slide.alignment || 'center',
                    display: 'flex',
                    alignItems: slide.vertical_alignment === 'bottom' ? 'flex-end' : slide.vertical_alignment === 'top' ? 'flex-start' : 'center',
                    justifyContent: slide.alignment === 'left' ? 'flex-start' : slide.alignment === 'right' ? 'flex-end' : 'center',
                    lineHeight: 1.2,
                    overflow: 'hidden',
                }}>
                    Sample Text
                </div>
                <div className="absolute bottom-0 left-0 right-0 px-2 py-1 bg-gray-900/75">
                    <p className="text-xs text-gray-300 truncate">
                        {SLIDE_TYPE_LABELS[slide.slide_type] || slide.name || slide.slide_type}
                    </p>
                </div>
            </div>
        );
    }

    render() {
        const { themes, loading, uploading, parsedTheme } = this.state;

        if (parsedTheme) {
            return (
                <ThemeUploadPicker
                    parsedTheme={parsedTheme}
                    onSave={this.handleSaveTheme}
                    onCancel={this.handleCancelUpload}
                />
            );
        }

        return (
            <div className="bg-white text-gray-800 rounded-sm shadow-md py-2 px-6 mb-4">
            <div className="max-w-4xl mx-auto p-6">
                <div className="flex items-center justify-between mb-6">
                    <h1 className="text-2xl font-bold text-gray-800">Themes</h1>
                    <label className="cursor-pointer bg-blue-500 text-white px-4 py-2 rounded-sm hover:bg-blue-600 text-sm font-medium">
                        {uploading ? 'Uploading...' : 'Upload .protheme'}
                        <input
                            type="file"
                            accept=".protheme"
                            className="hidden"
                            onChange={(e) => {
                                if (e.target.files[0]) this.handleFileUpload(e.target.files[0]);
                                e.target.value = '';
                            }}
                            disabled={uploading}
                        />
                    </label>
                </div>

                <div className="mb-6 bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <p className="text-sm font-medium text-gray-700 mb-2">How to export a theme from ProPresenter</p>
                    <ol className="text-sm text-gray-600 list-decimal list-inside space-y-1">
                        <li>In ProPresenter, make sure your theme includes a slide for each type: <strong>Body/Sermon</strong>, <strong>Scripture/Reference</strong>, and <strong>Song Lyrics</strong>. The scripture slide is used for both the verse text and the reference line.</li>
                        <li>Go to <strong>File</strong> &rarr; <strong>Export</strong> &rarr; <strong>Themes...</strong></li>
                        <li>Select the theme you want to export and save the <code>.protheme</code> file.</li>
                        <li>Upload it here using the button above.</li>
                    </ol>
                </div>

                {loading && <p className="text-gray-400">Loading themes...</p>}

                {!loading && themes.length === 0 && (
                    <div className="text-center py-12 bg-gray-50 rounded-lg">
                        <svg className="mx-auto mb-4" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#a0aec0" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="3" y="3" width="7" height="7" />
                            <rect x="14" y="3" width="7" height="7" />
                            <rect x="3" y="14" width="7" height="7" />
                            <rect x="14" y="14" width="7" height="7" />
                        </svg>
                        <p className="text-gray-500 mb-2">No themes uploaded yet</p>
                        <p className="text-gray-400 text-sm">Upload a .protheme file from ProPresenter to customize your slide designs.</p>
                    </div>
                )}

                <div className="space-y-4">
                    {themes.map(theme => (
                        <div key={theme.id} className={"border rounded-lg p-4 " + (theme.is_default ? "border-blue-400 bg-blue-50" : "border-gray-200")}>
                            <div className="flex items-center justify-between mb-3">
                                <div>
                                    <h2 className="text-lg font-semibold text-gray-800">
                                        {theme.name}
                                        {theme.is_default && (
                                            <span className="ml-2 text-xs bg-blue-500 text-white px-2 py-0.5 rounded-sm">Active</span>
                                        )}
                                    </h2>
                                    {theme.source_filename && (
                                        <p className="text-xs text-gray-400">{theme.source_filename}</p>
                                    )}
                                </div>
                                <div className="flex space-x-2">
                                    {!theme.is_default && (
                                        <button
                                            onClick={() => this.handleActivate(theme.id)}
                                            className="text-sm text-blue-500 hover:text-blue-700 px-3 py-1 border border-blue-300 rounded-sm"
                                        >
                                            Activate
                                        </button>
                                    )}
                                    <button
                                        onClick={() => this.handleDelete(theme.id, theme.name)}
                                        className="text-sm text-red-500 hover:text-red-700 px-3 py-1 border border-red-300 rounded-sm"
                                    >
                                        Delete
                                    </button>
                                </div>
                            </div>
                            <div className="flex space-x-3 overflow-x-auto">
                                {(theme.slides || []).map(slide => this.renderSlidePreview(slide))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
            </div>
        );
    }
}

export default ThemesLayout;
